import type { FilterOption } from '@/components/shared/DataTable/types';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';

/**
 * Parses a facility-process value into an array of process ids.
 *
 * companies-service returns it either as a JSON array of objects
 * (`'[{"id":"CUT_MAKE_TRIM"},...]'`) or, for legacy/aggregated shapes, as a
 * comma-separated string of ids (`'CUT_MAKE_TRIM,SEWING'`).
 */
export function processesArray(raw: string | null): string[] {
  if (!raw) return [];
  if (raw.trimStart().startsWith('[')) {
    try {
      return (JSON.parse(raw) as Array<{ id: string }>)
        .map(item => item.id)
        .filter(Boolean);
    } catch {
      // fall through to comma-split for legacy format
    }
  }
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

/**
 * Restricts the canonical grouped facility-process catalog to a subset of
 * keys (e.g. the facility processes actually assigned to a company, or
 * present on currently-loaded rows), preserving group nesting for
 * AppSelectGroup/AppSelectItem rendering. Empty groups are dropped.
 */
export function filterFacilityProcessGroups(
  groups: FacilityProcessGroupOption[],
  presentKeys: Iterable<string>,
): FacilityProcessGroupOption[] {
  const present = new Set(presentKeys);
  return groups
    .map(g => ({ ...g, options: g.options.filter(o => present.has(o.key)) }))
    .filter(g => g.options.length > 0);
}

/**
 * Same restriction as `filterFacilityProcessGroups`, but flattened into the
 * `indent`/`childKeys` shape DataTable filter checkbox lists expect (see
 * `FacilityProcessOptions({ as: 'data' })`). Keys not found in any canonical
 * group are appended flat at the end instead of being silently dropped.
 */
export function groupPresentFacilityProcesses(
  groups: FacilityProcessGroupOption[],
  presentKeys: Iterable<string>,
  labelForUnknown: (key: string) => string = key => key,
): (FilterOption & { indent?: number; childKeys?: string[] })[] {
  const remaining = new Set(presentKeys);
  const options: (FilterOption & { indent?: number; childKeys?: string[] })[] =
    [];
  for (const group of groups) {
    const presentChildren = group.options.filter(o => remaining.has(o.key));
    if (presentChildren.length === 0) continue;
    options.push({
      key: group.id,
      label: group.label,
      indent: 0,
      childKeys: presentChildren.map(o => o.key),
    });
    for (const o of presentChildren) {
      options.push({ key: o.key, label: o.label, indent: 1 });
      remaining.delete(o.key);
    }
  }
  for (const key of remaining) {
    options.push({ key, label: labelForUnknown(key) });
  }
  return options;
}
